import React, { Fragment, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";

import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Search from "../search";
import Loader from "../loader";
import shortenName from "../../helpers/shorten-name";

const formatAuditValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "[Empty]";
  }
  return String(value);
};

const AuditChangeDetails = ({ data }) => {
  const changes = data?.changes || [];

  if (!changes.length) {
    return null;
  }

  return (
    <div className="audit-change-details p-2">
      {changes.map((change, index) => {
        const field = Array.isArray(change.field)
          ? change.field.join(".")
          : change.field;

        let line;
        if (change.kind === "added") {
          line = `${field}: ${formatAuditValue(change.newValue)} (added)`;
        } else if (change.kind === "deleted") {
          line = `${field}: ${formatAuditValue(change.oldValue)} (deleted)`;
        } else {
          line = `${field}: ${formatAuditValue(
            change.oldValue
          )} → ${formatAuditValue(change.newValue)}`;
        }

        return (
          <div key={index} className="audit-change-line">
            {line}
          </div>
        );
      })}
    </div>
  );
};

const rowHasChanges = (row) => !!(row?.changes && row.changes.length);

const columns = [
  {
    name: "User",
    selector: "user.name",
    sortable: true,
    minWidth: "80px",
    maxWidth: "120px",
    cell: (row) => row.user ? shortenName(row.user.name) : "N/A",
  },
  {
    name: "Timestamp",
    selector: "timestamp",
    sortable: true,
    cell: (row) => {
      return row.timestamp ? moment(row.timestamp).format("M/D/YY - h:mm a") : "N/A";
    },
    minWidth: "140px",
    maxWidth: "150px",
  },
  {
    name: "Action",
    selector: "action",
    sortable: true,
    minWidth: "200px",
    maxWidth: "300px",
  },
  {
    name: "Details",
    selector: "details",
    sortable: false,
    wrap: true,
  },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }
    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    default:
      break;
  }
  return state;
};

const AuditTripModule = ({ refreshDataToken }) => {
  const { id } = useParams();

  const initialState = {
    loading: true,
    data: [],
    totalLength: 0,
    query: "",
    countPerPage: 10,
    currentPage: 1,
    sortBy: "timestamp",
    sortDirection: "desc",
  };
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = async (query, loadSilently) => {
    let loadingStatus = true;
    if (loadSilently) {
      loadingStatus = false;
    }

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    const token = await getAccessTokenSilently().catch(loginWithRedirect);

    const { data } = await ai.auth(token).get(`/api/trips/${id}/audit-table`, {
      params: {
        query: query.trim(),
        page: state.currentPage,
        countPerPage: state.countPerPage,
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
      },
    });

    dispatch({
      type: "dataDownloaded",
      payload: {
        data,
      },
    });
  };

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(
    (query) => {
      getData(query);
    },
    500,
  );

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });

    debouncedGetData(query);
  };

  const handleSort = (column, sortDirection) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  useEffect(() => {
    getData(state.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  useEffect(() => {
    getData(state.query, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshDataToken]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Audit Log"
        extraClasses="compact-view"
        rightColumn={
          <div
            id="trip-audit-table-actions-row"
            className="header-actions-row base-header-actions-row has-delete-btn opi-header"
          >
            <Search
              placeholder="Search"
              onSearch={handleSearch}
              query={state.query}
              className="trip-audit-table-search"
            />
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container data-table-compact-view shadow-none mt-2">
        <StickyHeaderDataTable
          columns={columns}
          data={state.data}
          keyField="_id"
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader/>}
          expandableRows
          expandableRowDisabled={(row) => !rowHasChanges(row)}
          expandableRowsComponent={<AuditChangeDetails />}
          persistTableHead
          highlightOnHover
          responsive
        />
      </div>
    </Fragment>
  );
};

export default AuditTripModule;
